<template>
    <div class="privacy-policy">
        <div class="content">
            <h1>PRIVACY POLICY</h1>
            <div v-for="(section, index) in state.policy" :key="index" class="info-block">
                <h2>{{ section.heading }}</h2>
                <div v-if="Array.isArray(section.content)">
                    <p v-for="(line, i) in section.content" :key="i">{{ line }}</p>
                </div>
                <p v-else>{{ section.content }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { reactive, onBeforeMount } from 'vue'

interface InfoBlock {
    heading: string;
    content: string | string[];
}

const state = reactive({
  policy: [] as InfoBlock[]
})

onBeforeMount(() => {
  state.policy = [
    {
      heading: 'Effective Date',
      content: '2021.01.05'
    },
    {
      heading: 'Introduction',
      content:
                'This Privacy Policy describes how LLC "Yaros Group" collects, uses, and protects the data of users of our application OfficeTime. We are committed to ensuring the privacy and security of all user data, including biometric information, in compliance with Apple’s App Store guidelines and applicable privacy laws.'
    },
    {
      heading: 'Data Collected',
      content: [
        'Biometric Data (Face Images): Used exclusively for employee check-in and check-out verification. The user voluntarily provides a facial image for authentication.',
        'Anonymous Identifiers (GUIDs): Used for employee identification within the system instead of personally identifiable information.',
        'Login Credentials: The App allows login to an existing company account using a phone number registered to the business account. This number is not linked to individual employees and does not have to be a personal number.',
        'Usage Data: We may collect non-personal information related to App performance and user experience.',
        'We do not collect sensitive personal information such as full names, addresses, phone numbers of employees, or government-issued identification numbers.'
      ]
    },
    {
      heading: 'How We Use Data',
      content: [
        'Allowing employees to check in and out via facial recognition.',
        'Ensuring proper functionality of the App’s authentication features.',
        'Improving security and fraud prevention within the system.'
      ]
    },
    {
      heading: 'Data Sharing and Third Parties',
      content:
                'We do not share, sell, or disclose any personal or biometric data to third parties. The App operates within a closed business environment where data is used exclusively by the company’s authorized users.'
    },
    {
      heading: 'Data Storage and Retention',
      content: [
        'When performing facial recognition, only an image is sent to the server for matching; this image is not linked to any specific employee record.',
        'The final recognition result in the app is matched using an anonymous GUID identifier, ensuring privacy and security.',
        'The App does not transmit or synchronize employee data across different accounts or companies.',
        'User data is retained only as long as necessary for the intended business operations and is securely deleted once no longer required.'
      ]
    },
    {
      heading: 'Security Measures',
      content: [
        'End-to-end encryption of biometric data transmission.',
        'Secure login authentication mechanisms.',
        'Regular security audits to ensure compliance with data protection standards.'
      ]
    },
    {
      heading: 'Compliance with Apple Guidelines',
      content:
                'This App complies with Apple’s App Store policies, including Guideline 5.1.1 regarding the collection and storage of sensitive user data. It also adheres to best practices for handling biometric information securely and ensuring user privacy.'
    },
    {
      heading: 'Contact Information',
      content: [
        'LLC "Yaros Group"',
        'info@yaros.kg',
        '720021, Kyrgyzstan, Bishkek, Chokmorov Street, 1'
      ]
    },
    {
      heading: 'Дата вступления в силу',
      content: '2021.01.05'
    },
    {
      heading: 'Введение',
      content:
        'Данная Политика конфиденциальности описывает, как ОсОО "Ярос групп" собирает, использует и защищает данные пользователей нашего приложения OfficeTime. Мы привержены обеспечению конфиденциальности и безопасности всех пользовательских данных, включая биометрическую информацию, в соответствии с правилами Apple App Store и действующими законами о защите данных.'
    },
    {
      heading: 'Собираемые данные',
      content: [
        'Биометрические данные (изображения лица): используются исключительно для отметки прихода и ухода сотрудников. Пользователь добровольно предоставляет изображение лица для идентификации.',
        'Анонимные идентификаторы (GUIDs): используются для идентификации сотрудников в системе без привязки к персональным данным.',
        'Данные для входа: Приложение предоставляет вход в существующую учетную запись компании, используя номер телефона, зарегистрированный для учетной записи. Этот номер не привязан к конкретному сотруднику и не обязан быть персональным.',
        'Данные об использовании: Мы можем собирать не персонализированную информацию, связанную с производительностью и удобством использования приложения.',
        'Мы не собираем чувствительную личную информацию, такую как полные имена, адреса, номера телефонов сотрудников или государственные идентификаторы.'
      ]
    },
    {
      heading: 'Использование данных',
      content: [
        'Возможность сотрудников отмечать приход и уход с работы с помощью распознавания лица.',
        'Обеспечение корректной работы системы аутентификации.',
        'Повышение безопасности и предотвращение мошенничества.'
      ]
    },
    {
      heading: 'Передача данных и третьи лица',
      content:
        'Мы не передаем, не продаем и не раскрываем персональные или биометрические данные третьим лицам. Приложение работает в закрытой бизнес-среде, где данные используются только авторизованными пользователями компании.'
    },
    {
      heading: 'Хранение и защита данных',
      content: [
        'При распознавании на сервер передается только изображение, оно не привязано к конкретному сотруднику.',
        'Итоговое распознавание в приложении происходит по анонимному идентификатору GUID, что обеспечивает конфиденциальность данных.',
        'Приложение не передает данные между различными учетными записями или компаниями.',
        'Данные хранятся только в течение срока, необходимого для работы приложения, и удаляются, когда в них больше нет необходимости.'
      ]
    },
    {
      heading: 'Меры безопасности',
      content: [
        'Шифрование передаваемых биометрических данных.',
        'Безопасная аутентификация при входе в систему.',
        'Регулярные проверки безопасности, чтобы соответствовать стандартам защиты данных.'
      ]
    },
    {
      heading: 'Соответствие требованиям Apple',
      content:
        'Данное приложение соответствует политикам App Store, включая Правило 5.1.1, регулирующее сбор и хранение конфиденциальных данных. Мы строго соблюдаем лучшие практики обработки биометрической информации и защиты персональных данных.'
    },
    {
      heading: 'Контактная информация',
      content: [
        'ОсОО "Ярос групп"',
        'info@yaros.kg',
        '720021, Кыргызстан, г. Бишкек, ул. Чокморова, 1'
      ]
    }
  ]
})
</script>

<style scoped>
.privacy-policy {
    font-family: 'OpenSans', sans-serif;
    height: 100vh;
    overflow-y: auto;
}

.privacy-policy .content {
    padding: 5vw;
}

.privacy-policy h1 {
    color: white;
}

.info-block {
    margin-bottom: 2rem;
}

.info-block h2 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
    color: white;
}

.info-block p {
    margin: 0.2rem 0;
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.6);
}
</style>
